/**
 * ReportDataProvider - Provides report metadata for popup windows
 * 
 * This component runs in the main window and handles POPUP_INIT requests
 * for report popups. It fetches the job and report template data via API,
 * and ensures the queue-based report fetching process is triggered.
 * 
 * Flow:
 * 1. Popup sends POPUP_INIT with jobId
 * 2. This provider fetches report templates (if not cached) and triggers queue-based report fetch
 * 3. Finds the job in the templates and sends back job + template + ship data
 */
import { useCallback, useState, useEffect, useRef } from 'react';
import { 
  useGetReportTemplates, 
  useCompleteReportFetchingProcess,
  Job,
  ReportTemplate,
} from '@ais-ui/websockets';
import { PopupType } from '@ais-ui/popups';
import {
  usePopupMessages,
  MainWindowActions,
  MessageType,
  PopupMessage,
} from './PopupEvents';
import { useSettings } from '../Settings/SettingsProvider';
import { useTimeZoneContext } from '@ais-ui/utils';

interface ActiveRequest {
  popupId: string;
  jobId: string;
}

export const ReportDataProvider = (): null => {
  const [activeRequest, setActiveRequest] = useState<ActiveRequest | null>(null);
  const { data: reportTemplates, isLoading: templatesLoading } = useGetReportTemplates(false);
  const { settings } = useSettings();
  const { timeZone } = useTimeZoneContext();
  
  // Track if we've already sent a response for the current request
  const sentResponseRef = useRef<string | null>(null);

  // Helper to get common settings for popups
  const getPopupSettings = useCallback(() => ({
    tableDensity: settings['ribbonPlugin.settings']?.table_density || 'compact',
  }), [settings]);

  // Use the queue-based report fetching process (same as main app)
  // This ensures data is available even if report wasn't viewed in main app first
  const jobIdToFetch = activeRequest?.jobId ?? '';
  const { 
    data: reportResult, 
    isLoading: reportLoading, 
    isError: reportError 
  } = useCompleteReportFetchingProcess(jobIdToFetch, timeZone);
  
  // Consider loading complete when no active request (nothing to load)
  const isReportLoading = activeRequest ? reportLoading : false;

  // When we have all data, find the job in templates and respond
  useEffect(() => {
    if (!activeRequest) return;
    if (templatesLoading || isReportLoading) return;
    
    // Don't send duplicate responses for the same request
    if (sentResponseRef.current === activeRequest.popupId) return;
    
    const { popupId, jobId } = activeRequest;

    // Find the job and its template from report templates
    let foundJob: Job | null = null;
    let foundTemplate: ReportTemplate | null = null;

    if (reportTemplates) {
      for (const template of reportTemplates) {
        const job = template.jobs.find((j) => j.uuid === jobId);
        if (job) {
          foundJob = job;
          foundTemplate = template;
          break;
        }
      }
    }

    // If job not found in templates, report error
    if (!foundJob || !foundTemplate) {
      MainWindowActions.sendInitResponse(popupId, {
        job: null,
        reportTemplate: null,
        report: null,
        shipData: [],
        settings: getPopupSettings(),
        error: `Job ${jobId} not found. It may have been deleted.`,
      });
      sentResponseRef.current = popupId;
      setActiveRequest(null);
      return;
    }

    // If report fetch failed, still send what we have
    if (reportError) {
      MainWindowActions.sendInitResponse(popupId, {
        job: foundJob,
        reportTemplate: foundTemplate,
        report: null,
        shipData: [],
        settings: getPopupSettings(),
        error: 'Failed to fetch report details',
      });
      sentResponseRef.current = popupId;
      setActiveRequest(null);
      return;
    }

    // Send complete response with job, template, and ship data from queue result
    MainWindowActions.sendInitResponse(popupId, {
      job: foundJob,
      reportTemplate: foundTemplate,
      report: null, // ParsedReportParams not needed - ship data comes from reportResult
      shipData: reportResult?.shipData ?? [],
      exportTypes: reportResult?.exportFormats,
      settings: getPopupSettings(),
    });
    
    sentResponseRef.current = popupId;
    setActiveRequest(null);
  }, [activeRequest, reportTemplates, templatesLoading, reportResult, isReportLoading, reportError, getPopupSettings]);

  // Listen for report popup init requests
  usePopupMessages(
    useCallback(
      (msg: PopupMessage) => {
        if (msg.type !== MessageType.POPUP_INIT) return;

        const payload = msg.payload as {
          popupType: string;
          popupId: string;
          params: {
            jobId?: string;
            requestShipData?: boolean;
          };
        };

        // Only handle report popup init requests
        if (payload.popupType !== PopupType.Report) return;

        const { popupId, params } = payload;
        const { jobId } = params;

        if (!jobId) {
          MainWindowActions.sendInitResponse(popupId, {
            job: null,
            reportTemplate: null,
            report: null,
            shipData: [],
            settings: getPopupSettings(),
            error: 'No job ID provided',
          });
          return;
        }

        // Reset the sent response tracker for new requests
        sentResponseRef.current = null;
        
        // Set active request to trigger data fetching
        setActiveRequest({ popupId, jobId });
      },
      [getPopupSettings]
    ),
    []
  );

  return null;
};
